import { useEffect, useState, useMemo, useCallback } from 'react';
import { messageService, type Message } from '../services/message.service';

interface UseMessagesResult {
  messages: Message[];
  sentMessages: Message[];
  loading: boolean;
  unreadCount: number;
  filteredMessages: Message[];
  activeFilter: MessageFilter;
  setActiveFilter: (value: MessageFilter) => void;
  markAsRead: (messageId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  toggleImportant: (messageId: string, isImportant: boolean) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
}

export type MessageFilter = 'all' | 'unread' | 'important' | 'sent';

export function useMessages(): UseMessagesResult {
  const [messages, setMessages] = useState<Message[]>([]);
  const [sentMessages, setSentMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeFilter, setActiveFilter] = useState<MessageFilter>('all');

  useEffect(() => {
    setLoading(true);

    const unsubscribeMessages = messageService.subscribeToUserMessages((nextMessages) => {
      setMessages(nextMessages);
      setLoading(false);
    });

    const unsubscribeUnread = messageService.subscribeToUnreadCount((count) => {
      setUnreadCount(count);
    });

    const unsubscribeSent = messageService.subscribeToSentMessages((msgs) => {
      setSentMessages(msgs);
    });

    return () => {
      unsubscribeMessages?.();
      unsubscribeUnread?.();
      unsubscribeSent?.();
    };
  }, []);

  const filteredMessages = useMemo(() => {
    if (activeFilter === 'sent') {
      return sentMessages;
    }
    switch (activeFilter) {
      case 'unread':
        return messages.filter((msg) => !msg.isRead);
      case 'important':
        return messages.filter((msg) => msg.isImportant);
      default:
        return messages;
    }
  }, [messages, sentMessages, activeFilter]);

  const markAsRead = useCallback(async (messageId: string) => {
    await messageService.markAsRead(messageId);
  }, []);

  const markAllAsRead = useCallback(async () => {
    await messageService.markAllAsRead();
  }, []);

  const toggleImportant = useCallback(async (messageId: string, isImportant: boolean) => {
    await messageService.toggleImportant(messageId, isImportant);
  }, []);

  const deleteMessage = useCallback(async (messageId: string) => {
    await messageService.deleteMessage(messageId);
  }, []);

  return {
    messages,
    sentMessages,
    loading,
    unreadCount,
    filteredMessages,
    activeFilter,
    setActiveFilter,
    markAsRead,
    markAllAsRead,
    toggleImportant,
    deleteMessage,
  };
}
